import { useState, useEffect } from "react";
import { useToast } from "../hooks/useToast";
import api from "../api/client";
import * as XLSX from "xlsx";

export default function PhysicalCount() {
  const { showToast } = useToast();
  const [branches, setBranches] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [branchId, setBranchId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [data, setData] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [counted, setCounted] = useState<Record<string, string>>({});

  useEffect(() => {
    api.get("/branches").then(r => setBranches(r.data.data));
    api.get("/locations").then(r => setLocations(r.data.data));
  }, []);

  const filteredLocations = locations.filter(l => !branchId || String(l.branch_id) === String(branchId));
  const getKey = (r: any) => `${r.sku}-${r.branch_name}-${r.location_name}`;
  const getDiff = (r: any) => { const c = counted[getKey(r)]; if (c === undefined || c === "") return ""; return (Number(c) - Number(r.qty_on_hand)).toFixed(2); };

  const load = () => {
    setLoading(true);
    api.get("/reports/physical-count-sheet", { params: { branch_id: branchId||undefined, location_id: locationId||undefined } })
      .then(r => { setData(r.data.data); setSummary(r.data.summary); setCounted({}); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const exportExcel = () => {
    const rows = data.map(r => ({
      SKU: r.sku,
      Product: r.product_name,
      Category: r.category,
      Branch: r.branch_name,
      Location: r.location_name,
      "System Qty": Number(r.qty_on_hand),
      Unit: r.unit,
      "Unit Cost": Number(r.cost_price || 0),
      "System Amount": Number(r.qty_on_hand) * Number(r.cost_price || 0),
      "Counted Qty": counted[getKey(r)] || "",
      Difference: getDiff(r),
      Notes: "",
      "As of Date (YYYY-MM-DD)": summary.as_of || "",
      "COA Account Code": "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Physical Count");
    XLSX.writeFile(wb, `physical-count-${summary.as_of || "sheet"}.xlsx`);
  };

  const print = () => window.print();

  const grouped: Record<string, any[]> = {};
  data.forEach(r => { if (!grouped[r.category]) grouped[r.category] = []; grouped[r.category].push(r); });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Physical Count Sheet</h1>
      </div>
      <div className="card" style={{ marginBottom:16 }} id="no-print">
        <div className="card-body">
          <div style={{ display:"flex", gap:12, alignItems:"flex-end", flexWrap:"wrap" }}>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label className="form-label">Branch</label>
              <select className="form-control" value={branchId} onChange={e => { setBranchId(e.target.value); setLocationId(""); }} style={{ width:180 }}>
                <option value="">All Branches</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label className="form-label">Location</label>
              <select className="form-control" value={locationId} onChange={e => setLocationId(e.target.value)} style={{ width:180 }}>
                <option value="">All Locations</option>
                {filteredLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <button className="btn btn-primary" type="button" onClick={load} disabled={loading}>{loading ? "Loading..." : "Filter"}</button>
            <button className="btn btn-secondary" type="button" onClick={() => { setBranchId(""); setLocationId(""); setData([]); }}>Clear</button>
          </div>
        </div>
      </div>
      {data.length > 0 && (
        <>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }} id="no-print">
            <span style={{ fontSize:13, color:"var(--tx2)" }}><strong>{summary.total}</strong> product(s) &mdash; {summary.as_of}</span>
            <div style={{ display:"flex", gap:8 }}>
              <button className="btn btn-secondary" type="button" onClick={exportExcel}>Export Excel</button>
              <button className="btn btn-primary" type="button" onClick={print}>Print Count Sheet</button>
            </div>
          </div>
          <div className="card" id="print-area">
            <div style={{ padding:"16px 20px", borderBottom:"1px solid var(--bdr)", display:"flex", justifyContent:"space-between" }}>
              <div><div style={{ fontSize:16, fontWeight:700 }}>Physical Count Sheet</div><div style={{ fontSize:12, color:"var(--tx3)" }}>As of {summary.as_of}</div></div>
              <div style={{ fontSize:12, color:"var(--tx3)", textAlign:"right" }}>
                {branchId ? branches.find(b => String(b.id)===branchId)?.name : "All Branches"}<br/>
                {locationId ? filteredLocations.find(l => String(l.id)===locationId)?.name : "All Locations"}
              </div>
            </div>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ background:"var(--sur2)" }}>
                  <th style={{ padding:"8px 12px", fontSize:10, fontWeight:600, color:"var(--tx3)", textTransform:"uppercase", textAlign:"left" }}>SKU</th>
                  <th style={{ padding:"8px 12px", fontSize:10, fontWeight:600, color:"var(--tx3)", textTransform:"uppercase", textAlign:"left" }}>Product</th>
                  <th style={{ padding:"8px 12px", fontSize:10, fontWeight:600, color:"var(--tx3)", textTransform:"uppercase", textAlign:"left" }}>Branch</th>
                  <th style={{ padding:"8px 12px", fontSize:10, fontWeight:600, color:"var(--tx3)", textTransform:"uppercase", textAlign:"left" }}>Location</th>
                  <th style={{ padding:"8px 12px", fontSize:10, fontWeight:600, color:"var(--tx3)", textTransform:"uppercase", textAlign:"right" }}>System Qty</th>
                  <th style={{ padding:"8px 12px", fontSize:10, fontWeight:600, color:"var(--tx3)", textTransform:"uppercase", textAlign:"right" }}>Unit Cost</th>
                  <th style={{ padding:"8px 12px", fontSize:10, fontWeight:600, color:"var(--tx3)", textTransform:"uppercase", textAlign:"right" }}>Amount</th>
                  <th style={{ padding:"8px 12px", fontSize:10, fontWeight:600, color:"var(--tx3)", textTransform:"uppercase", textAlign:"left" }}>Unit</th>
                  <th style={{ padding:"8px 12px", fontSize:10, fontWeight:600, color:"var(--tx3)", textTransform:"uppercase", textAlign:"right" }}>Counted Qty</th>
                  <th style={{ padding:"8px 12px", fontSize:10, fontWeight:600, color:"var(--tx3)", textTransform:"uppercase", textAlign:"right" }}>Difference</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(grouped).map(([cat, rows]) => (
                  <>
                    <tr key={cat} style={{ background:"var(--sur2)" }}>
                      <td colSpan={10} style={{ padding:"6px 12px", fontSize:10, fontWeight:700, color:"var(--tx3)", textTransform:"uppercase", letterSpacing:1 }}>{cat}</td>
                    </tr>
                    {rows.map((r, i) => {
                      const key = getKey(r);
                      const diff = getDiff(r);
                      return (
                        <tr key={i} style={{ borderBottom:"1px solid var(--bdr)" }}>
                          <td style={{ padding:"8px 12px", fontSize:11, fontFamily:"monospace", color:"var(--blue)" }}>{r.sku}</td>
                          <td style={{ padding:"8px 12px", fontSize:12, fontWeight:500 }}>{r.product_name}</td>
                          <td style={{ padding:"8px 12px", fontSize:11, color:"var(--tx3)" }}>{r.branch_name}</td>
                          <td style={{ padding:"8px 12px", fontSize:11, color:"var(--tx3)" }}>{r.location_name}</td>
                          <td style={{ padding:"8px 12px", fontSize:12, fontWeight:600, textAlign:"right" }}>{Number(r.qty_on_hand).toLocaleString()}</td>
                          <td style={{ padding:"8px 12px", fontSize:12, textAlign:"right" }}>{Number(r.cost_price||0).toFixed(2)}</td>
                          <td style={{ padding:"8px 12px", fontSize:12, textAlign:"right", fontWeight:600 }}>{(Number(r.qty_on_hand)*Number(r.cost_price||0)).toFixed(2)}</td>
                          <td style={{ padding:"8px 12px", fontSize:11, color:"var(--tx3)" }}>{r.unit}</td>
                          <td style={{ padding:"4px 8px", textAlign:"right" }} className="no-print">
                            <input type="number" style={{ width:80, textAlign:"right", border:"1px solid var(--bdr)", borderRadius:4, padding:"2px 6px", fontSize:12 }}
                              value={counted[key] || ""} onChange={e => setCounted(c => ({...c, [key]: e.target.value}))} />
                          </td>
                          <td style={{ padding:"8px 12px", fontSize:12, fontWeight:600, textAlign:"right", color: diff === "" ? "var(--tx3)" : Number(diff) === 0 ? "var(--green)" : "var(--red)" }}>
                            {diff === "" ? "-" : diff}
                          </td>
                        </tr>
                      );
                    })}
                  </>
                ))}
              </tbody>
            </table>
            <div style={{ padding:"24px 20px", display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:40, marginTop:16, borderTop:"1px solid var(--bdr)" }}>
              <div><div style={{ fontSize:11, color:"var(--tx3)", marginBottom:32 }}>Counted by</div><div style={{ borderTop:"1px solid #333", paddingTop:4, fontSize:11 }}>Signature</div></div>
              <div><div style={{ fontSize:11, color:"var(--tx3)", marginBottom:32 }}>Date</div><div style={{ borderTop:"1px solid #333", paddingTop:4, fontSize:11 }}>Date</div></div>
              <div><div style={{ fontSize:11, color:"var(--tx3)", marginBottom:32 }}>Approved by</div><div style={{ borderTop:"1px solid #333", paddingTop:4, fontSize:11 }}>Signature</div></div>
            </div>
          </div>
        </>
      )}
      <style>{`
        @media print {
          #no-print, .no-print { display: none !important; }
          body { background: white; }
          .card { box-shadow: none; border: 1px solid #ddd; }
        }
      `}</style>
    </div>
  );
}